import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ToastContext = createContext({ showToast: () => {} });

let externalToastHandler = null;

const TOAST_DURATION = 7000;
const MAX_VISIBLE_TOASTS = 3;

const generateId = (() => {
  let counter = 0;
  return () => {
    counter += 1;
    return `toast_${Date.now()}_${counter}`;
  };
})();

const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(880, context.currentTime); // A5

    gainNode.gain.setValueAtTime(0.0001, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.1, context.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.3);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start();
    oscillator.stop(context.currentTime + 0.35);

    oscillator.onended = () => {
      gainNode.disconnect();
      oscillator.disconnect();
      context.close().catch(() => {});
    };
  } catch (error) {
    console.warn('Notification sound unavailable:', error);
  }
};

export const NotificationToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const queueRef = useRef([]);
  const timeoutMapRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timeoutId = timeoutMapRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutMapRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback((toast) => {
    const toastWithId = {
      id: generateId(),
      title: toast.title || 'Notification',
      message: toast.message || '',
      type: toast.type || 'info',
      createdAt: Date.now()
    };

    queueRef.current.push(toastWithId);
    setToasts((prev) => {
      const next = [...prev, toastWithId].slice(-MAX_VISIBLE_TOASTS);
      return next;
    });

    playNotificationSound();

    const timeoutId = setTimeout(() => {
      removeToast(toastWithId.id);
      queueRef.current = queueRef.current.filter((item) => item.id !== toastWithId.id);
    }, TOAST_DURATION);

    timeoutMapRef.current.set(toastWithId.id, timeoutId);
  }, [removeToast]);

  useEffect(() => {
    externalToastHandler = showToast;
    return () => {
      externalToastHandler = null;
      timeoutMapRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutMapRef.current.clear();
      queueRef.current = [];
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto max-w-xs sm:max-w-sm bg-white shadow-2xl rounded-lg border border-gray-200 overflow-hidden transform animate-toast-enter`}
          >
            <div className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className={`w-2 rounded-full mt-1 ${toast.type === 'error' ? 'bg-red-500' : toast.type === 'success' ? 'bg-green-500' : toast.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'}`} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{toast.title}</p>
                  {toast.message && (
                    <p className="text-sm text-gray-600 mt-1 leading-snug">
                      {toast.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="h-1 bg-gray-100">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 animate-toast-progress" />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useNotificationToast = () => useContext(ToastContext);

export const pushToast = (toast) => {
  if (typeof window === 'undefined') return;
  if (externalToastHandler) {
    externalToastHandler(toast);
  }
};
